import Column from '@app/components/layout/Column'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { memo, useCallback, useEffect, useState } from 'react'
import { Tabs } from '@app/components/core/tabs/Tabs'
import { match } from 'ts-pattern'
import { PageLayout } from '../layout/PageLayout'
import { pages } from '@app/util/links'
import { useLocation } from 'wouter'
import { DiscoverHousingForms } from './housing-forms/DiscoverHousingForms'
import { DiscoverRegions } from './regions/DiscoverRegions'
import { DiscoverInitiatives } from './initiatives/DiscoverInitiatives'
import { DiscoverPlots } from './plots/DiscoverPlots'
import { useParamSearch, useParamState } from '@app/util/hooks/useParamState'
import { SearchBar } from '@app/components/blocks/searchBar/SearchBar'
import useMediaQuery from '@app/util/useMediaQuery'
import useTransform from '@app/util/useTransform'
import { useLocationSuggestions } from '@app/components/blocks/searchBar/useLocationSuggestions'
import { SuggestionsFetcher } from '@app/components/blocks/searchBar/SearchField'
import InitiativeDefIcon from '@app/assets/discovery/initiativen-default.svg'
import LocationDefIcon from '@app/assets/discovery/location-default.svg'
import RegionDefIcon from '@app/assets/discovery/regio-default.svg'
import HousingFormDefIcon from '@app/assets/discovery/woonvorm-default.svg'
import InitiativeActiveIcon from '@app/assets/discovery/initiativen-active-hover.svg'
import LocationActiveIcon from '@app/assets/discovery/location-active-hover.svg'
import RegionActiveIcon from '@app/assets/discovery/regio-active-hover.svg'
import HousingFormActiveIcon from '@app/assets/discovery/woonvorm-active-hover.svg'

const tabs = {
  initiatives: 'initiatives',
  locations: 'locations',
  regions: 'regions',
  housingForms: 'housingForms',
} as const
type Tabs = keyof typeof tabs

const views = {
  grid: 'grid',
  map: 'map',
} as const

export const Discover = ({ tab = tabs.initiatives }: { tab?: Tabs }) => {
  const [_location, navigate] = useLocation()
  const isMobile = useMediaQuery()
  const transformStyle = useTransform('center')
  const [language, _setLanguage] = useLanguage()
  const translations = i18n.languages[language].app.discoveryPage
  const [searchTerm, setSearchTerm] = useParamSearch()
  const [searchBarInput, setSearchBarInput] = useState(searchTerm)
  const [view, setValue, toggleValue] = useParamState(views, views.grid, 'view')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const locationSuggestions = useLocationSuggestions()

  const hasMapAndFilters = tab === tabs.initiatives || tab === tabs.locations

  useEffect(() => {
    setSearchBarInput(searchTerm)
  }, [searchTerm])

  const changeTab = (page: string) => {
    onResetSearch()
    navigate(page)
  }

  const onResetSearch = useCallback(() => {
    setSearchTerm('')
  }, [setSearchTerm])

  const onFiltersToggle = useCallback(() => {
    setFiltersOpen((prev) => !prev)
  }, [])

  const onFiltersClose = useCallback(() => {
    setFiltersOpen(false)
  }, [])

  useEffect(() => {
    if (isMobile) {
      setValue(views.grid)
    }
  }, [isMobile, setValue])

  const placeholder = isMobile
    ? translations.searchPlaceholder.mobile
    : match(tab)
        .with(
          tabs.initiatives,
          () => translations.searchPlaceholder.initiatives,
        )
        .with(tabs.locations, () => translations.searchPlaceholder.plots)
        .with(tabs.regions, () => translations.searchPlaceholder.regions)
        .with(
          tabs.housingForms,
          () => translations.searchPlaceholder.housingForms,
        )
        .exhaustive()

  const suggestionsFetcher = match(tab)
    .with(tabs.initiatives, () => locationSuggestions)
    .with(tabs.locations, (): SuggestionsFetcher => locationSuggestions)
    .with(tabs.regions, (): SuggestionsFetcher => locationSuggestions)
    .otherwise((): SuggestionsFetcher | undefined => undefined)

  return (
    <PageLayout gap="2rem">
      <Column
        alignItems="center"
        gap={isMobile ? '1.5rem' : '2.5rem'}
        style={{
          alignSelf: 'center',
          ...transformStyle,
          zIndex: 3,
          flexGrow: 0,
        }}
      >
        <Tabs.Group
          selectedTab={tab}
          style={{ gap: isMobile ? '1rem' : '2.5rem', ...transformStyle }}
        >
          <Tabs.HeaderTab
            icon={InitiativeDefIcon}
            activeIcon={InitiativeActiveIcon}
            label={translations.initiatives}
            tabKey={tabs.initiatives}
            href={pages.initiatives}
            onSelectTab={() => changeTab(pages.initiatives)}
          />
          <Tabs.HeaderTab
            icon={LocationDefIcon}
            activeIcon={LocationActiveIcon}
            label={translations.plots}
            tabKey={tabs.locations}
            href={pages.plots}
            onSelectTab={() => changeTab(pages.plots)}
          />
          <Tabs.HeaderTab
            icon={RegionDefIcon}
            activeIcon={RegionActiveIcon}
            label={translations.regions}
            tabKey={tabs.regions}
            href={pages.regions}
            onSelectTab={() => changeTab(pages.regions)}
          />
          <Tabs.HeaderTab
            icon={HousingFormDefIcon}
            activeIcon={HousingFormActiveIcon}
            label={translations.housingForms}
            tabKey={tabs.housingForms}
            href={pages.housingForms}
            onSelectTab={() => changeTab(pages.housingForms)}
          />
        </Tabs.Group>
        <SearchBar
          placeholder={placeholder}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          searchBarInput={searchBarInput}
          setSearchBarInput={setSearchBarInput}
          currentView={view}
          onSwitchView={hasMapAndFilters ? toggleValue : undefined}
          onFiltersOpen={hasMapAndFilters ? onFiltersToggle : undefined}
          suggestionsFetcher={suggestionsFetcher}
        />
      </Column>
      <MemorizedDiscoverTabWrapper
        tab={tab}
        view={view}
        searchTerm={searchTerm}
        onResetSearch={onResetSearch}
        filtersOpen={filtersOpen}
        onFiltersClose={onFiltersClose}
      />
    </PageLayout>
  )
}

const DiscoverTabWrapper = ({
  tab,
  view,
  searchTerm,
  onResetSearch,
  filtersOpen,
  onFiltersClose,
}: {
  tab: Tabs
  view: 'grid' | 'map'
  searchTerm: string
  onResetSearch: () => void
  filtersOpen: boolean
  onFiltersClose: () => void
}) => {
  return (
    <Column alignItems="stretch" gap="2rem">
      {match(tab)
        .with(tabs.initiatives, () => (
          <DiscoverInitiatives
            view={view}
            searchTerm={searchTerm}
            onResetSearch={onResetSearch}
            filtersOpen={filtersOpen}
            onFiltersClose={onFiltersClose}
          />
        ))
        .with(tabs.locations, () => (
          <DiscoverPlots
            view={view}
            searchTerm={searchTerm}
            onResetSearch={onResetSearch}
            filtersOpen={filtersOpen}
            onFiltersClose={onFiltersClose}
          />
        ))
        .with(tabs.regions, () => (
          <DiscoverRegions
            searchTerm={searchTerm}
            onResetSearch={onResetSearch}
          />
        ))
        .with(tabs.housingForms, () => (
          <DiscoverHousingForms
            searchTerm={searchTerm}
            onResetSearch={onResetSearch}
          />
        ))
        .exhaustive()}
    </Column>
  )
}
const MemorizedDiscoverTabWrapper = memo(DiscoverTabWrapper)
